import React, { useEffect, useState } from "react";
import { Select, message } from "antd";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import GenericTable from "../../../Components/Tables/GenericTable";
import fuelIcon from "../../../Assets/Icons/local_gas_station.svg";
import paymentIcon from "../../../Assets/Icons/payments.svg";
import creditIcon from "../../../Assets/Icons/garage_money.svg";
import "./style.css";
import BarChart from "../../../Components/Charts/BarChart";
import { getFuelVendorDashboard } from "../../../API/FuelVendor/Dashboard";
import InitialsAvatar from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const formatCurrency = (value) =>
  `$${(parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getUniqueFilters = (data, key) =>
  [...new Set(data.map((item) => item[key]))].map((value) => ({
    text: value,
    value,
  }));

const VendorDashboard = () => {
  const [timeFilter, setTimeFilter] = useState("Monthly");
  const [stats, setStats] = useState(null);
  const [fuelSaleAnalytics, setFuelSaleAnalytics] = useState({
    labels: [],
    spend: [],
  });
  const [recentTransactions, setRecentTransactions] = useState([]);

  const fetchDashboard = async (period) => {
    try {
      const res = await getFuelVendorDashboard(period);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;

        setStats(data?.stats || null);
        if (!period) {
          setTimeFilter(
            data?.fuel_sale_analytics?.period
              ? data.fuel_sale_analytics.period.charAt(0).toUpperCase() +
                  data.fuel_sale_analytics.period.slice(1)
              : "Monthly",
          );
        }
        setFuelSaleAnalytics({
          labels: data?.fuel_sale_analytics?.by_fuel_type?.labels || [],
          spend: (
            data?.fuel_sale_analytics?.by_fuel_type?.total_amount || []
          ).map((v) => parseFloat(v) || 0),
        });
        setRecentTransactions(data?.recent_transactions || []);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load dashboard data."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load dashboard data."),
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleTimeFilterClick = (tab) => {
    setTimeFilter(tab);
    fetchDashboard(tab.toLowerCase());
  };

  const dataSource = recentTransactions.map((item, index) => ({
    key: item.id ?? index,
    date: item.date || item.transaction_date || item.created_at || "-",
    txnId: item.txn_id || item.transaction_id || item.request_id || "-",
    driver: item.driver_name || item.driver || "-",
    driverAvatar: resolveAvatarUrl(item.driver_avatar),
    vehicleId: item.vehicle_id || item.vehicle_registration || "-",
    fleetManager: item.fleet_company_name || item.fleet_manager || item.company_name || "-",
    fleetAvatar: resolveAvatarUrl(item.fleet_company_avatar),
    fuelType: item.fuel_type || item.fuel_type_name || "-",
    amount: formatCurrency(item.amount),
    rawAmount: parseFloat(item.amount) || 0,
    status: item.status
      ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
      : "-",
  }));

  const fuelTypeFilters = getUniqueFilters(dataSource, "fuelType");
  const statusFilters = getUniqueFilters(dataSource, "status");

  const txnColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    { title: "Txn ID", dataIndex: "txnId", key: "txnId", enableSearch: true },
    {
      title: "Driver Name",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
      render: (name, record) => (
        <span className="fv-dashboard__driver-link">
          <InitialsAvatar
            src={record.driverAvatar}
            name={name}
            className="fv-dashboard__avatar"
            style={{ fontSize: 10 }}
          />
          {name}
        </span>
      ),
    },
    {
      title: "Vehicle Id",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Fleet Manager",
      dataIndex: "fleetManager",
      key: "fleetManager",
      enableSearch: true,
      render: (name, record) =>
        record.fleetAvatar ? (
          <span className="fv-dashboard__fleet-cell">
            <img
              src={record.fleetAvatar}
              alt={name}
              className="fv-dashboard__avatar"
            />
            {name}
          </span>
        ) : (
          <span className="fv-dashboard__fleet-cell">
            <span className="fv-dashboard__fleet-icon">
              {name?.charAt(0)?.toUpperCase() || "-"}
            </span>
            {name}
          </span>
        ),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (text) => <span className="fuel-type-badge">{text}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.rawAmount - b.rawAmount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
  ];

  return (
    <div className="fv-dashboard manager-dashboard">
      <div className="fv-dashboard__stats">
        <GenericStatCard
          title="Total Fuel Sold"
          value={`${(parseFloat(stats?.total_fuel_sold?.value) || 0).toLocaleString()} ${stats?.total_fuel_sold?.unit || "L"}`}
          {...(stats?.total_fuel_sold?.percentage != null && {
            percentage: `${stats.total_fuel_sold.percentage}`,
          })}
          isIncrease={stats?.total_fuel_sold?.is_increase}
          subtitle="vs. last month"
          icon={fuelIcon}
        />
        <GenericStatCard
          title="Total Transactions"
          value={stats?.total_transactions?.value ?? 0}
          {...(stats?.total_transactions?.percentage != null && {
            percentage: `${stats.total_transactions.percentage}`,
          })}
          isIncrease={stats?.total_transactions?.is_increase}
          subtitle="vs. last month"
          icon={paymentIcon}
        />
        <GenericStatCard
          title="Amount Owed"
          value={formatCurrency(stats?.amount_owed?.value)}
          {...(stats?.amount_owed?.percentage != null && {
            percentage: `${stats.amount_owed.percentage}`,
          })}
          isIncrease={stats?.amount_owed?.is_increase}
          subtitle="vs. last month"
          icon={creditIcon}
        />
      </div>

      <div className="row fuel-sales-row">
        <div className="graph-card">
          <div className="chart-controls-header-simple">
            <div className="time-filter-pills">
              {["Daily", "Monthly", "Yearly"].map((tab) => (
                <button
                  key={tab}
                  className={`pill-button ${timeFilter === tab ? "active" : ""}`}
                  onClick={() => handleTimeFilterClick(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="bar-chart-container fueldata-graph">
            <BarChart
              graphData={{
                labels: fuelSaleAnalytics.labels,
                spend: fuelSaleAnalytics.spend,
              }}
              lengthKeys={1}
              colourTheme={["#4F46E5"]}
              graphTitle="Fuel Sale Analytics"
              selectTenByDefault={false}
              units={["$"]}
              classNameCSS="quantity-to-use"
              grayzoned={true}
            />
          </div>
        </div>
      </div>

      <GenericTable
        title="Fuel Transactions"
        dataSource={dataSource}
        columns={txnColumns}
        enableRowSelection={false}
      />
    </div>
  );
};

export default VendorDashboard;
